import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import './EditPMOModal.scss';

const EditPMOModal = ({
  open,
  handleClose,
  projectId,
  projectName,
  onSaveSuccess,
  baseURL,
  token,
}) => {
  const [pmoData, setPmoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { snackbarOpen } = useSnackbar();

  // List of categories that should only accept numbers
  const numericCategories = [
    'Jira Progress (%)',
    'For Scrum Project: Average no. of sprints that BAs are ahead of the Development',
    'Agile Scrum Projects: Stories Completed vs Planned in a Sprint (%)',
    'Avg weekend working in the project (Days/Resource/Month)',
  ];

  useEffect(() => {
    if (open && projectId) {
      fetchEditData();
    }
  }, [open, projectId]);

  const fetchEditData = async () => {
    setLoading(true);
    try {
      // Clean up double slashes just in case baseURL already has trailing slashes
      const cleanURL = `${baseURL}/${projectId}/edit`.replace(/([^:]\/)\/+/g, '$1');
      const response = await fetch(cleanURL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          token: token,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const responseJson = await response.json();
      const responseData = responseJson?.data?.data || responseJson?.data;

      if (responseData && Array.isArray(responseData)) {
        setPmoData(responseData);
      } else {
        setPmoData([]);
      }
    } catch (error) {
      console.error('Failed to fetch PMO details', error);
      snackbarOpen('Failed to fetch PMO details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (planId, field, value) => {
    setPmoData((prev) =>
      prev.map((item) => {
        if (item.pmo_plan_id === planId) {
          let finalValue = value;

          // Enforce numbers-only for specific categories
          if (field === 'value' && numericCategories.some(cat => item.pmo_category?.includes(cat))) {
            // Remove everything except numbers and a decimal point
            finalValue = String(value).replace(/[^0-9.]/g, '');

            // Prevent entering multiple decimal points
            const parts = finalValue.split('.');
            if (parts.length > 2) {
              finalValue = parts[0] + '.' + parts.slice(1).join('');
            }
          }

          return { ...item, [field]: finalValue };
        }
        return item;
      }),
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = pmoData.map((item) => ({
        pmo_plan_id: item.pmo_plan_id,
        value: item.type === 'date' && item.value ? dayjs(item.value).format('YYYY-MM-DD') : item.value,
        comments: item.comments,
      }));

      const cleanURL = `${baseURL}/${projectId}/edit`.replace(/([^:]\/)\/+/g, '$1');

      const response = await fetch(cleanURL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          token: token,
        },
        body: JSON.stringify({ pmoPayload: payload }),
      });

      if (!response.ok) {
        throw new Error('Failed to update the value');
      }

      snackbarOpen('Health check value updated successfully', 'success');
      onSaveSuccess();
      handleClose();
    } catch (error) {
      console.error('Failed to update PMO details', error);
      snackbarOpen('Failed to update the value', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth className="pmo-edit-modal">
      <DialogTitle className="modal-title">
        Edit {projectName}
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }} disabled={isSaving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className="modal-content-body">
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', fontFamily: 'Mukta' }}>Loading...</div>
        ) : (
          <div className="pmo-grid">
            <div className="pmo-grid-header">
              <div className="col-category">Categories</div>
              <div className="col-status">Status</div>
              <div className="col-comments">Comments</div>
            </div>

            {pmoData.map((row) => {
              const isNumericField = numericCategories.some(cat => row.pmo_category?.includes(cat));

              return (
                <div className="pmo-grid-row" key={row.pmo_plan_id}>
                  <div className="col-category">{row.pmo_category}</div>
                  <div className="col-status">
                    {row.type === 'dropdown' && (
                      <Select
                        size="small"
                        fullWidth
                        value={row.value}
                        displayEmpty
                        onChange={(event) => handleInputChange(row.pmo_plan_id, 'value', event.target.value)}
                        disabled={isSaving}
                      >
                        <MenuItem value="" disabled>Select</MenuItem>
                        {row.options.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    )}

                    {row.type === 'date' && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={row.value ? dayjs(row.value) : null}
                          onChange={(newValue) => handleInputChange(row.pmo_plan_id, 'value', newValue)}
                          slotProps={{ textField: { size: 'small', fullWidth: true, placeholder: 'Select' } }}
                          disabled={isSaving}
                        />
                      </LocalizationProvider>
                    )}

                    {row.type === 'text' && (
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Enter status"
                        value={row.value}
                        onChange={(event) => handleInputChange(row.pmo_plan_id, 'value', event.target.value)}
                        disabled={isSaving}
                        inputProps={{
                          inputMode: isNumericField ? 'decimal' : 'text',
                        }}
                      />
                    )}
                  </div>
                  <div className="col-comments">
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Add comments"
                      value={row.comments}
                      onChange={(event) => handleInputChange(row.pmo_plan_id, 'comments', event.target.value)}
                      disabled={isSaving}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>

      <DialogActions className="modal-actions">
        <Button onClick={handleClose} className="btn-cancel" disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" className="btn-save" disabled={isSaving || loading}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditPMOModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  projectName: PropTypes.string.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  baseURL: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
};

export default EditPMOModal;
